import React, { useContext } from "react";
import ArrowTxtBtn from '../../../../../img/icon-txt-btn-arrow.svg';
import ArrowButton from '../../../../../img/icon-btn-arrow-right.svg';
import PlayButton from '../../../../../img/button-play-circle.svg';
import LoadingPanel from './loadingPanel';
import {getFormattedDate} from '../helpers/formattedDate';
import {getOpenSeats, sortCourses, getOpenSeatSortCategoryKey, getEligibleCategory, is165Course } from "../helpers/coursesHelper";

// api imports
import { useGetCoursesSectionQuery } from '../api/courses';
import { changeActiveCourse } from '../redux/activeCourseName/activeCourseNameSlice'

// context import
import { Context } from '../courseTemplates';

/**
 * @param {integer} courseRecordId - Displaid Course's template record id
 * @param {integer} onlineCourseRecordId - Displaid Course's online course record id
 * @param {integer} courseNumber - Displaid Course's template record id
 * @param {boolean} onlineOnly - Whether course is online only
 * @param {boolean} courseRibbonShouldRedirect - Whether we should override the "Digital Option" portion of the ribbon to point at another page.
 * @param {string} courseRibbonText - Text to display for the course ribbon.
 * @param {string} courseRibbonUrl - The URL for the course ribbon to link to.
 * @returns
 */
function UpcomingCourseDates({courseRecordId, onlineCourseRecordId, courseNumber, onlineOnly, courseRibbonShouldRedirect, courseRibbonText, courseRibbonUrl}) {
    // get our global context variables
    const value = useContext(Context);
    const { loggedIn, koisUrl, userHighestCategory, onlineCourseLink, showDigitalTeaserDate } = value;

    // get the future courses with the new RTK query
    // Using a query hook automatically fetches data and returns query values
    const { currentData: futureCoursesCurrentData, isFetching: futureCoursesIsFetching } = useGetCoursesSectionQuery({templateId: courseRecordId, sections: 'future'});

    // if we have our future courses data check what the highest eligible category is
    if(futureCoursesCurrentData){
        var eligibleCat = getEligibleCategory(futureCoursesCurrentData.futureCourses, userHighestCategory);
    }
    // if we have future courses data and have set the highest eligible category use it.
    const highestEligibleCategory = eligibleCat ? eligibleCat : userHighestCategory;

    // flag to make sure we only show the shortest waitlist once, this is reliant on the courses being sorted correctly via our coursesHelper sort function
    let shortestWaitlistFlag = true;
    let shortestWaitlistInfoText = 'Shortest waitlist';
    // flag to make sure we append the online course to the ribbon and leave room for it
    const hasOnlineCourse = onlineCourseRecordId ? true : false;

    // flag to know if we should show the digital course teaser
    let showDigitalTeaser = false;

    // check if the course has open seats, return how many open seats if so
    // else check if the course has a wait list and return the waitlist length
    function getInfoText (course) {
        // if the user is not logged in dont show any info
        if(!loggedIn){
            return '';
        }
        
        const openSeats = getRibbonOpenSeats(course);

        if(openSeats > 0){
            return openSeats + ' spots available';
        // if this is the first course in the ribbon that doesn't have open seats, then it is the shortest waitlist according to the sort
        } else if(shortestWaitlistFlag){
            shortestWaitlistFlag = false;
            return shortestWaitlistInfoText;
        // any remaining courses should be waitlisted if sorted correctly
        } else {
            return 'Waitlist available'
        }
    }

    // check if the course has open seats, return how many open seats if so
    // else check if the course has a wait list and return the waitlist length
    function getActionLink (course) {
        const href = '#' + (course.Timber_Post.anchor_index == undefined ? 'future-courses' : 'course' + course.Timber_Post.anchor_index)

        const openSeats = getRibbonOpenSeats(course);
        
        return (
            <a className="text-btn-arrow " href={href}>{openSeats ? 'Register' : 'Join Waitlist'}<span className="link-arrow"><ArrowTxtBtn /></span></a>
        )

    }

    const getRibbonOpenSeats = (course) => {
        // get the open seat key for thie users highest eligible category
        const openSeatsKey = getOpenSeatSortCategoryKey(highestEligibleCategory);
        // if the highestEligibleCategory is Clinical Instructor, combine the Clinical Instructor and Mentor open seats for spot availability
        if(highestEligibleCategory === 'Clinical Instructor' && (getOpenSeats(course, highestEligibleCategory) + getOpenSeats(course, "Mentor") > 0)){
            return getOpenSeats(course, highestEligibleCategory) + getOpenSeats(course, "Mentor");
        } else if(course.Timber_Post[openSeatsKey] > 0){
            return parseInt(course.Timber_Post[openSeatsKey]);
        }
    }

    // Component level JSX return statement ******************************************************
    return (
        <>
            <section className="course-upcoming-dates">
                <div className="container">
                    { futureCoursesIsFetching &&
                        <LoadingPanel loadText={'Loading Upcoming Courses'}/>
                    }
                    { (futureCoursesCurrentData || onlineOnly) && !futureCoursesIsFetching &&
                    <>
                        <h2 className="course-upcoming-dates__headline">Upcoming Course Dates</h2>
                        <ul className="course-upcoming-dates__list">
                            {/* sort the courses for the ribbon by date with open seats, then waitlist length (not date dependent) */}
                            {futureCoursesCurrentData?.futureCourses && sortCourses(futureCoursesCurrentData.futureCourses, highestEligibleCategory, loggedIn).map((futureCourse, index) => {
                                // we only want to show three courses in the ribbon, if there is an online course we only want to show two and then the online option
                                let maxIndex = 3;
                                if(hasOnlineCourse || courseRibbonShouldRedirect){
                                    maxIndex = 2;
                                }
                                // continue until we hit the max number of courses to show
                                if(index < maxIndex){
                                    let infoText = getInfoText(futureCourse);
                                    // This is a check to see if all the courses are sold out, we check if the first class in the loop is the shortest waitlis and the course is 165 and we are past the show digital teaser date
                                    if(index === 0 && infoText === shortestWaitlistInfoText && is165Course(courseNumber) && Date.now()> showDigitalTeaserDate){
                                        showDigitalTeaser = false;
                                    }
                                    // if we are going to show the digital teaser, and are on the 3rd list item, don't show anything
                                    if(showDigitalTeaser && index === 2){
                                        return;
                                    }else{
                                        return (
                                            <li>
                                                <p className="seat-availability-text">{infoText}</p>
                                                <h3 className="date">{getFormattedDate(futureCourse.Timber_Post.course_start_timestamp, futureCourse.Timber_Post.course_end_timestamp)}</h3>
                                                {getActionLink(futureCourse)}
                                                <a className="more-options-link" href="#future-courses">More Options</a>
                                            </li>
                                        )
                                    }
                                }
                            })}
                            {courseRibbonShouldRedirect ? (
                                <li>
                                    <p className="seat-availability-text">Start Your Journey</p>
                                    <h3 className="digital-option">{courseRibbonText}</h3>
                                    <a className="more-options-link" href={courseRibbonUrl}>
                                        View Course Dates
                                    </a>
                                </li>
                            ) : hasOnlineCourse ? (
                                <li>
                                    <p className="seat-availability-text">Available now</p>
                                    <h3 className="digital-option"><span className="play-button"><PlayButton/></span>Digital Option</h3>
                                    <a className="text-btn-arrow " href='#online-course-anchor'>Register<span className="link-arrow"><ArrowTxtBtn /></span></a>
                                </li>
                            ) : null}
                            {showDigitalTeaser &&
                                <li>
                                    <p className="seat-availability-text">Be the first to know!</p>
                                    <h3 className="digital-option"><span className="play-button"><PlayButton/></span>Digital Option</h3>
                                    <a className="text-btn-arrow " href={onlineCourseLink} target="_blank">Find out more<span className="link-arrow"><ArrowTxtBtn /></span></a>
                                </li>
                            }
                            {!onlineOnly &&
                                <li className="calendar-link">
                                    <a class="text-btn-arrow2" href="#future-courses">View All Upcoming Dates<span class="link-arrow"><ArrowButton/></span></a>
                                </li>
                            }
                        </ul>
                    </>
                    }
                </div>
            </section>
        </>
    )
}

export default UpcomingCourseDates;